import { useState } from 'react';
import { ApiError } from '@/lib/api';
import { providerAppsApi, type ConnectionView, type ProviderAppView } from '@/lib/connections';
import { Button, Field, Notice, TextButton } from '@/components/ui';
import { ConfirmDialog } from '@/components/ConfirmDialog';

/**
 * Registering the OAuth application an organisation connects as.
 *
 * THIS IS THE FIRST STEP NOW, and the screen is written for the person who has
 * to do it — an IT administrator with the provider's console open in another
 * tab, not the facilities manager the rest of this app is written for. So it
 * names the exact pages, the exact permissions, and shows the redirect URI to
 * copy rather than describing how to build one.
 *
 * The redirect URI is the single most common way this fails: both Google and
 * Microsoft compare it byte-for-byte, on consent AND again on the code
 * exchange, and a trailing slash or an http:// is a dead redirect with no
 * useful error.
 */

interface Guidance {
  where: string;
  steps: string[];
  clientIdHint: string;
  clientSecretHint: string;
}

const GUIDANCE: Record<string, Guidance> = {
  google: {
    where: 'Google Cloud Console, in a project your organisation owns',
    steps: [
      'Enable the Google Calendar API and the Admin SDK API. The room directory comes from the Admin SDK — enabling only Calendar is why discovery returns nothing.',
      'Create an OAuth client of type Web application.',
      'Add the redirect URI below to that client, exactly as shown.',
      'On the consent screen, add every permission listed under "What we will ask for" on this card. The directory one covers both rooms and people — the Directory screen is what goes blank without it.',
      'Whoever clicks Connect afterwards must be a Workspace administrator — the directory permission does nothing otherwise.',
    ],
    clientIdHint: 'Ends in .apps.googleusercontent.com',
    clientSecretHint: 'Usually starts GOCSPX-. Stored encrypted; we never show it again.',
  },
  microsoft: {
    where: 'Microsoft Entra admin centre → App registrations → New registration',
    steps: [
      'Register the app for accounts in this organisational directory only.',
      'Add the redirect URI below as a Web platform redirect, exactly as shown.',
      'Under API permissions → Microsoft Graph → Application permissions, add Place.Read.All, Calendars.ReadWrite and User.Read.All. Then add the delegated User.Read, which is used once to record who connected. User.Read.All is only for the Directory screen; without it everything else still works.',
      'You can grant admin consent here, or leave it — Connect asks your administrator to approve them as its second step. Adding the permissions above is the part only you can do.',
      'Under Certificates & secrets, create a client secret and copy its Value — not its Secret ID. Note the expiry date; when it lapses, everything stops until it is replaced here.',
    ],
    clientIdHint: 'The Application (client) ID from the Overview page — a GUID',
    clientSecretHint:
      'The secret VALUE, not the Secret ID. Stored encrypted; we never show it again.',
  },
};

export function ProviderAppSetup({
  connection,
  app,
  onSaved,
}: {
  connection: ConnectionView;
  app: ProviderAppView | null;
  onSaved: () => void | Promise<void>;
}) {
  const guidance = GUIDANCE[connection.provider];
  const usingOwn = app?.source === 'organization';
  // No credentials anywhere for this provider: the form below is the only way
  // it can ever be connected, so it is shown in full.
  const usingDeployment = app?.source === 'deployment';
  const [editing, setEditing] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Removal is confirmed in a dialog of ours, not the browser's. */
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await providerAppsApi.save(connection.provider, clientId, clientSecret);
      setClientId('');
      setClientSecret('');
      setEditing(false);
      await onSaved();
    } catch (err) {
      // The server's message names the field that is wrong — it is written for
      // this screen, so it is shown as-is rather than replaced with a generic.
      setError(err instanceof ApiError ? err.message : 'We could not save those credentials.');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setConfirmingRemove(false);
    setSaving(true);
    setError(null);
    try {
      await providerAppsApi.remove(connection.provider);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not remove that application.');
    } finally {
      setSaving(false);
    }
  }

  /*
   * ZenSpace's own application covers this provider: nothing is asked for and
   * nothing is offered. The card says only what the person can act on, and for
   * almost everyone the OAuth application is not one of those things.
   *
   * The override still exists — `PUT /calendar/apps/:provider`, stored on
   * `calendar_provider_apps` — for the customer whose security team requires
   * their own registration. It is simply not advertised on a screen most
   * people reach for a different reason.
   */
  if (usingDeployment) return null;

  // The organisation registered its own, and is not replacing it: one quiet
  // line and the two actions. An administrator revisiting this card is usually
  // here for something else.
  if (usingOwn && !editing) {
    return (
      <div className="mt-[20px] border-t border-divider pt-[20px]">
        <p className="zs-eyebrow m-0">Your {connection.display_name} application</p>
        <p className="mt-[8px] mb-0 font-mono text-sub break-all text-ink">
          {connection.app_client_id}
        </p>
        <p className="mt-[4px] mb-[12px] text-caption text-muted">
          {connection.app_configured_by
            ? `Added by ${connection.app_configured_by}`
            : 'Added by your organisation'}
          {connection.app_updated_at
            ? ` · last changed ${new Date(connection.app_updated_at).toLocaleDateString()}`
            : ''}
        </p>
        {error && <Notice tone="broken" title={error} />}
        <div className="flex flex-wrap gap-[14px]">
          <TextButton type="button" onClick={() => setEditing(true)}>
            Replace credentials
          </TextButton>
          <TextButton type="button" disabled={saving} onClick={() => setConfirmingRemove(true)}>
            Remove application
          </TextButton>
        </div>

        <ConfirmDialog
          open={confirmingRemove}
          title={`Remove the ${connection.display_name} application?`}
          confirmLabel="Remove application"
          cancelLabel="Keep it"
          busy={saving}
          onConfirm={() => void remove()}
          onCancel={() => !saving && setConfirmingRemove(false)}
        >
          <ul className="m-0 flex list-none flex-col gap-[8px] p-0">
            <li className="flex gap-[8px]">
              <span aria-hidden="true">✓</span>
              Nothing in your {connection.display_name} account changes. The registration itself
              stays exactly where it is.
            </li>
            <li className="flex gap-[8px]">
              <span aria-hidden="true">–</span>
              The client id and secret are removed from here, so they have to be entered again
              before you can connect.
            </li>
          </ul>
        </ConfirmDialog>
      </div>
    );
  }

  return (
    <div className="mt-[20px] border-t border-divider pt-[20px]">
      <p className="zs-eyebrow m-0">
        {usingOwn ? 'Replace credentials' : 'Use your own application'}
      </p>

      {!usingOwn && (
        <>
          <p className="mt-[8px] mb-[4px] max-w-[70ch] text-body text-ink">
            We connect to {connection.display_name} as your application. Your IT team registers
            it once, in {guidance?.where ?? 'your provider console'}, and keeps control of it —
            including the ability to revoke us without involving ZenSpace.
          </p>
          {guidance && (
            <ol className="mt-[12px] mb-0 flex list-decimal flex-col gap-[8px] pl-[20px]">
              {guidance.steps.map((step) => (
                <li key={step} className="max-w-[70ch] text-sub text-muted">
                  {step}
                </li>
              ))}
            </ol>
          )}
        </>
      )}

      {/* Offered to copy, never described. A retyped redirect URI is a dead
          redirect, and the error the provider gives for it names nothing. */}
      {app?.redirect_uri && (
        <div className="mt-[16px] rounded-button border border-divider bg-page p-[14px]">
          <p className="zs-eyebrow m-0">Redirect URI to register</p>
          <p className="mt-[6px] mb-[8px] font-mono text-sub break-all text-ink">
            {app.redirect_uri}
          </p>
          <p className="m-0 text-caption text-muted">
            Must match exactly — no trailing slash, and https. Both providers compare it when
            you grant consent and again a moment later.
          </p>
        </div>
      )}

      <form className="mt-[16px] flex flex-col gap-[14px]" onSubmit={(e) => void save(e)}>
        <Field
          label="Client id"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          hint={guidance?.clientIdHint}
          autoComplete="off"
          spellCheck={false}
          required
        />
        <Field
          label="Client secret"
          // `password` so it is not left legible on a shared screen, and so a
          // browser does not offer to remember it as an ordinary field.
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          hint={guidance?.clientSecretHint}
          autoComplete="new-password"
          spellCheck={false}
          required
        />

        {error && <Notice tone="broken" title={error} />}

        <div className="flex flex-wrap items-center gap-[12px]">
          <Button
            type="submit"
            tone="primary"
            busy={saving}
            disabled={!clientId.trim() || !clientSecret.trim()}
          >
            Save application
          </Button>
          {usingOwn && (
            <TextButton
              type="button"
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
            >
              Cancel
            </TextButton>
          )}
        </div>
      </form>
    </div>
  );
}
